import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

export const AverageMetricsBar = React.memo(function AverageMetricsBar({
  shotGroups,
  highlightDistance = null,
  selectedShots = [],
  showAverage = true
}) {
  const averages = useMemo(() => {
    // If specific shots are selected, calculate metrics for those shots only
    // Otherwise, use all included shots
    let shotsToAnalyze;

    if (selectedShots.length > 0) {
      shotsToAnalyze = shotGroups.flatMap(g =>
        g.shots.filter(shot =>
          selectedShots.some(s => s.groupId === g.id && s.shotNumber === shot.shotNumber)
        )
      );
    } else if (showAverage) {
      shotsToAnalyze = shotGroups.flatMap(g => g.shots.filter(s => s.included));
    } else {
      return null; // Don't show if average is hidden and no specific shots selected
    }

    if (shotsToAnalyze.length === 0) {
      return null;
    }

    const avg = (key) => {
      const sum = shotsToAnalyze.reduce((acc, shot) => acc + (shot.metrics[key] || 0), 0);
      return sum / shotsToAnalyze.length;
    };

    return {
      clubSpeed: avg('clubSpeed'),
      ballSpeed: avg('ballSpeed'),
      smashFactor: avg('smashFactor'),
      launchAngle: avg('launchAngle'),
      spinRate: avg('spinRate'),
      spinAxis: avg('spinAxis'),
      sideSpin: avg('sideSpin'),
      carry: avg('carry'),
      total: avg('total'),
      landingAngle: avg('landingAngle'),
      height: avg('height'),
      attackAngle: avg('attackAngle'),
      offlineDistance: avg('offlineDistance'),
      clubPath: avg('clubPath'),
      faceAngle: avg('faceAngle'),
      dynamicLoft: avg('dynamicLoft'),
      count: shotsToAnalyze.length,
    };
  }, [shotGroups, selectedShots, showAverage]);

  if (!averages) {
    return null;
  }

  const metrics = [
    // Distance metrics
    { key: 'carry', label: 'Carry', value: averages.carry, unit: 'yds', decimals: 0 },
    { key: 'total', label: 'Total', value: averages.total, unit: 'yds', decimals: 0 },
    { key: 'offlineDistance', label: 'Offline', value: averages.offlineDistance, unit: 'yds', decimals: 0 },

    // Speed & efficiency
    { key: 'clubSpeed', label: 'Club Speed', value: averages.clubSpeed, unit: 'mph', decimals: 1 },
    { key: 'ballSpeed', label: 'Ball Speed', value: averages.ballSpeed, unit: 'mph', decimals: 1 },
    { key: 'smashFactor', label: 'Smash Fac.', value: averages.smashFactor, unit: '', decimals: 2 },

    // Launch conditions
    { key: 'launchAngle', label: 'Launch Ang.', value: averages.launchAngle, unit: '°', decimals: 1 },
    { key: 'spinRate', label: 'Spin Rate', value: averages.spinRate, unit: 'rpm', decimals: 0 },
    { key: 'spinAxis', label: 'Spin Axis', value: averages.spinAxis, unit: '°', decimals: 1 },
    { key: 'sideSpin', label: 'Side Spin', value: averages.sideSpin, unit: 'rpm', decimals: 0 },

    // Trajectory
    { key: 'height', label: 'Height', value: averages.height, unit: 'ft', decimals: 0 },
    { key: 'landingAngle', label: 'Land. Ang.', value: averages.landingAngle, unit: '°', decimals: 1 },

    // Club delivery
    { key: 'attackAngle', label: 'Attack Ang.', value: averages.attackAngle, unit: '°', decimals: 1 },
    { key: 'clubPath', label: 'Club Path', value: averages.clubPath, unit: '°', decimals: 1 },
    { key: 'faceAngle', label: 'Face Angle', value: averages.faceAngle, unit: '°', decimals: 1 },
    { key: 'dynamicLoft', label: 'Dyn. Loft', value: averages.dynamicLoft, unit: '°', decimals: 1 },
  ];

  return (
    <div className="bg-surface border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-primary">
          {selectedShots.length > 0 ? 'Selected Shot Metrics' : 'Average Metrics'}
        </h3>
        <span className="text-xs text-text-muted">Based on {averages.count} shot{averages.count !== 1 ? 's' : ''}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-8 gap-3">
        {metrics.map(metric => {
          const isHighlighted = highlightDistance && (
            (highlightDistance === 'carry' && metric.key === 'carry') ||
            (highlightDistance === 'total' && metric.key === 'total')
          );

          const hasValue = metric.value != null && !isNaN(metric.value) && metric.value !== 0;

          return (
            <div
              key={metric.key}
              className={`bg-background border rounded-lg p-3 text-center hover:border-primary/50 transition-all ${
                isHighlighted
                  ? 'border-primary border-2 ring-2 ring-primary/20 shadow-lg scale-105'
                  : 'border-border'
              } ${!hasValue ? 'opacity-50' : ''}`}
            >
              <div className={`text-xs mb-1 uppercase tracking-wide ${
                isHighlighted ? 'text-primary font-semibold' : 'text-text-muted'
              }`}>
                {metric.label}
              </div>
              <div className={`text-lg font-bold font-mono ${
                isHighlighted ? 'text-primary text-xl' : hasValue ? 'text-primary' : 'text-text-muted'
              }`}>
                {hasValue ? metric.value.toFixed(metric.decimals) : '—'}
              </div>
              {metric.unit && (
                <div className={`text-xs mt-1 ${
                  isHighlighted ? 'text-primary font-medium' : 'text-text-muted'
                }`}>
                  {metric.unit}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

AverageMetricsBar.propTypes = {
  shotGroups: PropTypes.arrayOf(
    PropTypes.shape({
      shots: PropTypes.arrayOf(
        PropTypes.shape({
          included: PropTypes.bool.isRequired,
          metrics: PropTypes.shape({
            clubSpeed: PropTypes.number,
            ballSpeed: PropTypes.number,
            smashFactor: PropTypes.number,
            launchAngle: PropTypes.number,
            spinRate: PropTypes.number,
            spinAxis: PropTypes.number,
            sideSpin: PropTypes.number,
            carry: PropTypes.number,
            total: PropTypes.number,
            landingAngle: PropTypes.number,
            height: PropTypes.number,
            attackAngle: PropTypes.number,
            offlineDistance: PropTypes.number,
            clubPath: PropTypes.number,
            faceAngle: PropTypes.number,
            dynamicLoft: PropTypes.number,
          }).isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  highlightDistance: PropTypes.oneOf(['carry', 'total']),
};
